// @mui
import { Box, Grid, Link, Stack, Divider, Container, Typography, IconButton } from '@mui/material';

import { useTranslate } from 'src/locales';
import { getPoopDataLocalizedPath } from 'src/pages/poop-data/seo';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_ICON_NAME,
  GOOGLE_PLAY_ICON_NAME,
} from 'src/constants/app-store';
import {
  BUSINESS_NAME,
  CONTACT_EMAIL,
  BUSINESS_ADDRESS,
  CONTACT_KAKAO_ID,
  BUSINESS_CATEGORY,
  CONTACT_KAKAO_URL,
  CONTACT_EMAIL_MAILTO,
  CUSTOMER_CENTER_PHONE,
  BUSINESS_REPRESENTATIVE,
  CUSTOMER_CENTER_PHONE_TEL,
  BUSINESS_REGISTRATION_NUMBER,
  MAIL_ORDER_BUSINESS_REPORT_NUMBER,
} from 'src/constants/contact';

// components
import { Logo } from '../../components/logo';
// routes
import { PUBLIC_PATH } from '../../routes/paths';
import { Iconify } from '../../components/iconify';

// ----------------------------------------------------------------------

export const _socials = [
  {
    value: 'instagram',
    name: 'Instagram',
    icon: 'ant-design:instagram-filled' as const,
    color: '#E02D69',
    path: 'https://www.instagram.com/barabom.me',
  },
  {
    value: 'blog',
    name: 'Blog',
    icon: 'simple-icons:bloglovin' as const,
    color: '#00AAEC',
    path: 'https://blog.naver.com/0610studio',
  },
  {
    value: 'appStore',
    name: 'AppStore',
    icon: APP_STORE_ICON_NAME,
    color: '#00AAEC',
    path: APP_STORE_URL,
  },
  {
    value: 'playStore',
    name: 'PlayStore',
    icon: GOOGLE_PLAY_ICON_NAME,
    color: '#00AAEC',
    path: GOOGLE_PLAY_URL,
  },
];

export default function Footer() {
  const { t, currentLang } = useTranslate('public');

  const businessInfo = [
    {
      label: t('footer.business.name'),
      value: BUSINESS_NAME[currentLang.value],
    },
    {
      label: t('footer.business.representative'),
      value: BUSINESS_REPRESENTATIVE[currentLang.value],
    },
    {
      label: t('footer.business.registrationNumber'),
      value: BUSINESS_REGISTRATION_NUMBER,
    },
    {
      label: t('footer.business.address'),
      value: BUSINESS_ADDRESS[currentLang.value],
    },
    {
      label: t('footer.business.category'),
      value: BUSINESS_CATEGORY[currentLang.value],
    },
    {
      label: t('footer.business.mailOrderReportNumber'),
      value: MAIL_ORDER_BUSINESS_REPORT_NUMBER,
    },
  ];

  const links = [
    {
      headline: t('footer.sections.barabom'),
      children: [
        { name: t('footer.links.intro'), href: '/' },
        { name: t('footer.links.dataCenter'), href: PUBLIC_PATH.appInfo },
        { name: t('footer.links.poopData'), href: getPoopDataLocalizedPath(currentLang.value) },
        { name: t('footer.links.inquiry'), href: PUBLIC_PATH.inquiry },
      ],
    },
    {
      headline: t('footer.sections.terms'),
      children: [
        { name: t('footer.links.termsOfUse'), href: PUBLIC_PATH.terms.used },
        { name: t('footer.links.privacy'), href: PUBLIC_PATH.terms.privacy },
        { name: t('footer.links.location'), href: PUBLIC_PATH.terms.loc },
        { name: t('footer.links.paidService'), href: PUBLIC_PATH.terms.pay },
      ],
    },
    {
      headline: t('footer.sections.contact'),
      children: [
        {
          name: t('footer.contact.phone', { customerCenterPhone: CUSTOMER_CENTER_PHONE }),
          href: CUSTOMER_CENTER_PHONE_TEL,
        },
        {
          name: t('footer.contact.email', { contactEmail: CONTACT_EMAIL }),
          href: CONTACT_EMAIL_MAILTO,
        },
        {
          name: t('footer.contact.kakao', { contactKakaoId: CONTACT_KAKAO_ID }),
          href: CONTACT_KAKAO_URL,
        },
      ],
    },
  ];

  const mainFooter = (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: 'background.default',
      }}
    >
      <Divider />

      <Container sx={{ pt: 10 }}>
        <Grid
          container
          justifyContent={{
            xs: 'center',
            md: 'space-between',
          }}
          sx={{
            textAlign: {
              xs: 'center',
              md: 'left',
            },
          }}
        >
          <Grid size={12} sx={{ mb: 3 }}>
            <Logo sx={{ mx: { xs: 'auto', md: 'inherit' } }} />
          </Grid>

          <Grid size={{ xs: 8, md: 3 }}>
            <Typography variant="body2" sx={{ pr: { md: 5 } }}>
              {t('footer.description.line1')}
              <br />
              <br />
              {t('footer.description.line2')}
              <br />
              {t('footer.description.line3')}
              <br />
              <br />
              {t('footer.description.line4')}
            </Typography>

            <Stack
              spacing={1}
              direction="row"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              sx={{
                mt: 5,
                mb: { xs: 5, md: 0 },
              }}
            >
              {_socials.map((social) => (
                <IconButton key={social.name} href={social.path}>
                  <Iconify icon={social.icon} />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Stack
              spacing={5}
              justifyContent="space-between"
              direction={{ xs: 'column', md: 'row' }}
            >
              {links.map((list) => (
                <Stack
                  key={list.headline}
                  spacing={2}
                  alignItems={{ xs: 'center', md: 'flex-start' }}
                >
                  <Typography component="div" variant="overline">
                    {list.headline}
                  </Typography>

                  {list.children.map((link) => (
                    <Link key={link.name} href={link.href} color="inherit" variant="body2">
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Stack
          spacing={1}
          sx={{
            mt: 10,
            pb: 5,
            color: 'text.secondary',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography variant="caption" component="div">
            {businessInfo.map((item) => (
              <Box component="span" key={item.label} sx={{ display: 'block' }}>
                {item.label}: {item.value}
              </Box>
            ))}
          </Typography>

          <Typography variant="caption" component="div">
            {t('footer.copyright')}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );

  return mainFooter;
  // return isHome ? simpleFooter : mainFooter;
}
